import { useEffect, useState } from "react";
import { X, HeartHandshake } from "lucide-react";
import client from "../lib/api";

/**
 * QuitGateModal.
 *
 * The humane exit. Four screens, in order:
 *
 *   pause  - step away, keep everything
 *   lower  - shrink the bar
 *   adjust - change the shape
 *   quit   - close for good, requires the deliberate written phrase
 *
 * On the quit path the exact typed phrase is required, but a near
 * miss NEVER errors. The API returns a gentle re-prompt in Klair's
 * voice and the modal renders it inline. This is on purpose: a
 * person quitting may be in a low moment, and a 400 that reads as
 * refusal would be the wrong shape of interaction.
 */
export function QuitGateModal({ pledgeId, onClose, onArchived }) {
  const [screens, setScreens] = useState([]);
  const [selected, setSelected] = useState("quit");
  const [typedPhrase, setTypedPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [reprompt, setReprompt] = useState(null);
  const [careRail, setCareRail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get(`/accountability/pledge/${pledgeId}/exit-screens`).then((r) => {
      setScreens(r.data?.screens || []);
    }).catch(() => setScreens([]));
  }, [pledgeId]);

  const submitQuit = async () => {
    setBusy(true); setReprompt(null); setCareRail(null);
    try {
      const { data } = await client.post(
        `/accountability/pledge/${pledgeId}/quit`,
        { typed_phrase: typedPhrase, reason: reason || undefined },
      );
      if (data.status === "confirmed_quit") { onArchived?.(); return; }
      if (data.status === "care_rail") { setCareRail(data.message); return; }
      if (data.status === "needs_confirmation") { setReprompt(data.message); return; }
    } catch (_e) {
      setReprompt("Klair could not close this just now. Please try again.");
    } finally { setBusy(false); }
  };

  const chosen = screens.find((s) => s.id === selected);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "hsl(var(--klareet-ink) / 0.35)" }}
      data-testid="quit-gate-overlay"
    >
      <div className="k-card w-full max-w-xl" style={{ maxHeight: "90vh", overflowY: "auto" }} data-testid="quit-gate-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="k-label">close this pledge</div>
          <button onClick={onClose} className="k-btn k-btn-ghost !p-2" data-testid="quit-gate-close">
            <X size={16} />
          </button>
        </div>

        <p className="klareet-serif mb-4" style={{ fontSize: "1.05rem", lineHeight: 1.4 }}>
          There is no wrong way to leave a goal. Four kinds of exit, all of them yours.
        </p>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {screens.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelected(s.id); setReprompt(null); setCareRail(null); }}
              className={`k-btn !justify-start ${selected === s.id ? "k-btn-primary" : "k-btn-ghost"}`}
              data-testid={`quit-gate-tab-${s.id}`}
            >
              {s.title}
            </button>
          ))}
        </div>

        {chosen && (
          <div className="k-warm-card p-4" data-testid={`quit-gate-body-${chosen.id}`}>
            <p className="text-sm" style={{ lineHeight: 1.5 }}>{chosen.body}</p>
          </div>
        )}

        {selected === "quit" && (
          <div className="mt-4">
            <div className="k-label mb-2">a reason, if you want to leave one</div>
            <textarea
              className="k-input klareet-serif"
              rows={2}
              maxLength={800}
              placeholder="optional; kept private"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="quit-gate-reason"
            />
            <div className="k-label mt-4 mb-2">to close, type these exact words. I gave up</div>
            <input
              className="k-input klareet-mono"
              value={typedPhrase}
              onChange={(e) => { setTypedPhrase(e.target.value); setReprompt(null); }}
              placeholder="I gave up"
              data-testid="quit-gate-phrase"
            />
            {reprompt && (
              <div className="k-quiet mt-3 text-sm" data-testid="quit-gate-reprompt">
                {reprompt}
              </div>
            )}
            {careRail && (
              <div className="k-warm-card mt-3 p-3 text-sm" data-testid="quit-gate-care-rail">
                <HeartHandshake size={14} className="inline mr-2" />
                {careRail}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={submitQuit}
                disabled={busy || !typedPhrase.trim()}
                className="k-btn k-btn-primary"
                data-testid="quit-gate-submit"
              >
                {busy ? "Closing." : "Close for good"}
              </button>
            </div>
          </div>
        )}

        {selected !== "quit" && chosen && (
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="k-btn k-btn-ghost" data-testid={`quit-gate-back-${chosen.id}`}>
              Keep this pledge open
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
